import { useApolloClient } from "@apollo/client";
import { Capacitor } from "@capacitor/core";
import { getDataOrNull, omitDeep } from "@msys/common";
import { getFormattedFloat } from "@msys/formatting";
import {
  CollapseSection,
  getFormattedNumber,
  isImage,
  LabeledValue,
  ModalContent,
  ModalDialog,
  ModalTitle,
  processAttachment,
} from "@msys/ui";
import AddIcon from "@mui/icons-material/Add";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageNotSupportedIcon from "@mui/icons-material/ImageNotSupported";
import InfoIcon from "@mui/icons-material/Info";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  FormControlLabel,
  FormGroup,
  IconButton,
  Slider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTolgee, useTranslate } from "@tolgee/react";
import { Form, Formik, FormikProps } from "formik";
import { capitalize, intersection, isArray, omit, uniqueId } from "lodash-es";
import React from "react";
import * as Yup from "yup";
import {
  AskWhen,
  AskWhom,
  DefineItemPropsOverridesEntryInput,
  EnterItemProps2ValueEntry,
  Props2,
  Props2AllowedValuesNumber,
  Props2AllowedValuesText,
} from "../../../../clients/graphqlTypes.js";
import { CheckboxField } from "../../../commons/form-fields/CheckboxField.js";
import { FormikError } from "../../../commons/form-fields/FormikError.js";
import { SelectMultipleField } from "../../../commons/form-fields/SelectMultipleField.js";
import {
  AttachmentPhotoUploaderWithEditor,
  AttachmentPhotoUploaderWithEditorRef,
} from "../../attachments/AttachmentPhotoUploaderWithEditor.js";
import { AttachmentUploader } from "../../attachments/AttachmentUploader.js";
import { Attachment, getRotatedUrl } from "../../attachments/helpers.js";
import { PropertyGalleryBox } from "../boxes/PropertyGalleryBox.js";
import {
  getValuesMediaBool,
  ValueMediaBool,
} from "../form-fields/PropertyValuesMediaBoolField.js";
import { ItemPropsOverrideFragment } from "../properties.generated.js";
import {
  AllowedValue,
  getAllowedValues,
  getPropertyType,
  getPropValue,
  isArrayType,
  isComputedProp,
  isNonComputedProp,
  isNumberType,
  isTextType,
  PropType,
  setPropAllowedValues,
  setPropValue,
} from "../properties.js";
import { PropertyField } from "../PropertyField.js";
import { usePropertyHelpers } from "../usePropertyHelpers.js";
import { useQuestionContol } from "../useQuestionContol.js";
import {
  ItemPropsCustomizeOverrideModal_ItemFragment,
  useItemPropsCustomizeOverrideModalQuery,
} from "./ItemPropsCustomizeOverrideModal.generated.js";

export interface FormValues {
  questionControl: Array<{ askWhom: AskWhom[]; askWhen: AskWhen[] }> | null;
  defaultValue: string | string[] | number | number[] | null | undefined;
  clientVisibility: boolean | null;
  allowedValuesExclude: string[] | number[];
  allowedValuesMedia: {
    valueAsString: string;
    media: Attachment | null;
  }[];
}

interface Props {
  templateId: string;
  itemId: string;
  versionNumber: number;
  override: ItemPropsOverrideFragment | null;
  propertyKey: string;
  handleSave: (
    newOverrides: DefineItemPropsOverridesEntryInput | null
  ) => Promise<void>;
  handlePrevious: null | (() => void);
  handleNext: null | (() => void);
  handleClose: () => void;
}

export const ItemPropsCustomizeOverrideModal = (props: Props) => {
  const client = useApolloClient();
  const query = useItemPropsCustomizeOverrideModalQuery({
    client,
    variables: {
      docId: props.templateId,
      itemId: props.itemId,
      versionNumber: props.versionNumber,
      applyOverrides: false,
    },
  });
  const quoteTemplate = getDataOrNull(
    query.data?.quoteTemplateVersion
  )?.quoteTemplate;
  const item = quoteTemplate?.item?.[0];

  if (!item) return null;

  return (
    <ItemPropsCustomizeOverrideInnerModal
      key={
        props.propertyKey
      } /*important to set to recreate form after a property change within a single item*/
      item={item}
      {...props}
    />
  );
};

function ItemPropsCustomizeOverrideInnerModal({
  templateId,
  itemId,
  item,
  override,
  propertyKey,
  handleSave,
  handlePrevious,
  handleNext,
  handleClose,
}: Props & { item: ItemPropsCustomizeOverrideModal_ItemFragment }) {
  const { t } = useTranslate(["Global", "QuoteItem"]);

  const property = item.props2.find(p => p.key === propertyKey);
  if (!property) throw new Error("Not found property");
  if (isComputedProp(property))
    throw new Error("Cannot customize computed property");

  const propertyType = getPropertyType(property);

  const initialValues: FormValues = React.useMemo(
    () => ({
      defaultValue:
        override?.valueAsJson !== null && override?.valueAsJson !== undefined
          ? JSON.parse(override.valueAsJson)
          : undefined,
      clientVisibility:
        override?.clientVisibility !== null &&
        override?.clientVisibility !== undefined
          ? override.clientVisibility
          : null,
      allowedValuesExclude:
        override?.allowedValuesExclude !== null &&
        override?.allowedValuesExclude !== undefined
          ? propertyType === "NUMBER"
            ? override.allowedValuesExclude.map(v => parseFloat(v))
            : override.allowedValuesExclude
          : [],
      allowedValuesMedia:
        override?.allowedValuesMedia
          .map(avm => omitDeep(avm, "__typename"))
          .map(avm => ({
            ...avm,
            media: avm.media ?? null,
          })) ?? [],
      questionControl:
        override?.questionControl !== undefined &&
        override?.questionControl !== null
          ? omitDeep(override.questionControl, "__typename")
          : null,
    }),
    [
      override?.allowedValuesExclude,
      override?.allowedValuesMedia,
      override?.clientVisibility,
      override?.questionControl,
      override?.valueAsJson,
      propertyType,
    ]
  );

  const propAllowedValues: AllowedValue[] = React.useMemo(
    () => getAllowedValues(property),
    [property]
  );

  const propValuesMediaBool: ValueMediaBool[] = React.useMemo(
    () => getValuesMediaBool(property),
    [property]
  );

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        defaultValue: Yup.mixed()
          .nullable()
          .optional()
          .label(t("Default value", { ns: "QuoteItem" }))
          .test(
            "shouldBeInAllowedValues",
            t("Not in allowed values", { ns: "QuoteItem" }),
            function (
              value: string | string[] | number | number[] | null | undefined
            ) {
              if (!propAllowedValues.length) return true;
              // @ts-ignore
              const valuesExclude = this.parent.allowedValuesExclude;
              if (!valuesExclude) return true;

              const defaultValue =
                value !== undefined ? value : getPropValue(property);
              if (
                defaultValue === null ||
                defaultValue === undefined ||
                (isArray(defaultValue) && !defaultValue.length)
              )
                return true;

              const allowedValues = propAllowedValues
                .filter(
                  propAllowedValue =>
                    !valuesExclude.includes(propAllowedValue.value)
                )
                .map(av => av.value);

              if (
                isNonComputedProp(property) &&
                (isTextType(property) || isNumberType(property)) &&
                !isArrayType(property)
              ) {
                return allowedValues.includes(defaultValue as string | number);
              }
              if (
                isNonComputedProp(property) &&
                (isTextType(property) || isNumberType(property)) &&
                isArrayType(property)
              ) {
                return (defaultValue as string[] | number[]).every(dv =>
                  allowedValues.includes(dv)
                );
              }

              return true;
            }
          ),
        allowedValuesExclude: Yup.array(Yup.mixed()).test(
          "atLeastOneShouldBeSelected",
          t("At least one value is required", {
            ns: "Global",
          }),
          function (values: any[] | undefined) {
            if (!values || !propAllowedValues.length) return true;
            const allowedValues = propAllowedValues.filter(
              propAllowedValue => !values.includes(propAllowedValue.value)
            );
            return allowedValues.length > 0;
          }
        ),
        questionControl: Yup.array(
          Yup.object().shape({
            askWhen: Yup.array(Yup.string()),
            askWhom: Yup.array(Yup.string()),
          })
        ).nullable(),
        clientVisibility: Yup.boolean().nullable(),
        allowedValuesMedia: Yup.array(
          Yup.object().shape({
            valueAsString: Yup.string(),
            media: Yup.mixed().nullable(),
          })
        ),
      }),
    [propAllowedValues, property, t]
  );

  const handleSubmit = async (values: FormValues) => {
    if (!hasPropValuesOverride(values)) {
      await handleSave(null);
    }

    await handleSave({
      key: propertyKey,
      questionControl: values.questionControl,
      clientVisibility: values.clientVisibility,
      allowedValuesExclude: values.allowedValuesExclude.map(v => v.toString()),
      allowedValuesMedia: values.allowedValuesMedia.map(avm =>
        omitDeep(avm, "id", "__typename")
      ),
      valueAsJson:
        values.defaultValue !== undefined
          ? JSON.stringify(values.defaultValue)
          : undefined,
    });
  };

  const formId = React.useMemo(() => uniqueId(), []);

  const { askWhomOptions, askWhenOptions } = useQuestionContol();

  const onAllowedValueChange = async (
    formikProps: FormikProps<FormValues>,
    checked: boolean,
    allowedValue: AllowedValue
  ) => {
    const oldValue = formikProps.values.allowedValuesExclude ?? [];
    const newValue = checked
      ? oldValue.filter(v => v !== allowedValue.value)
      : [...oldValue, allowedValue.value];
    await formikProps.setFieldValue("allowedValuesExclude", newValue);
    await formikProps.setFieldTouched("defaultValue");

    // adjusting default values too
    let newProperty = property;
    if (formikProps.values.defaultValue !== undefined) {
      newProperty = setPropValue(newProperty, formikProps.values.defaultValue);
    }
    if (newValue && newValue.length > 0) {
      newProperty = setPropAllowedValues(
        newProperty,
        allowedValue => !newValue.includes(getAllowedValue(allowedValue))
      );
    }
    const propertyValue = getPropValue(newProperty);
    const allowedValues = getAllowedValues(newProperty).map(av => av.value);

    if (isArray(propertyValue)) {
      const newPropertyValue = intersection(propertyValue, allowedValues);
      if (newPropertyValue.length < propertyValue.length) {
        await formikProps.setFieldValue("defaultValue", newPropertyValue);
      }
    } else {
      if (
        propertyValue !== null &&
        !allowedValues.includes(propertyValue as string | number)
      ) {
        await formikProps.setFieldValue("defaultValue", null);
      }
    }
  };

  const onAllowedValueMediaChange = async (
    formikProps: FormikProps<FormValues>,
    valueAsString: string,
    media: Attachment | null | undefined
  ) => {
    let newValue = formikProps.values.allowedValuesMedia.filter(
      v => v.valueAsString !== valueAsString
    );
    if (media !== undefined) {
      newValue = [...newValue, { valueAsString: valueAsString, media }];
    }
    await formikProps.setFieldValue("allowedValuesMedia", newValue);
  };

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {formikProps => (
        <ModalDialog handleClose={handleClose}>
          <ModalTitle
            title={
              <Stack direction={"row"} spacing={1}>
                <span>{t("Customise property", { ns: "QuoteItem" })}</span>
                <Tooltip
                  title={
                    <Box component="span" sx={{ whiteSpace: "pre-line" }}>
                      {t(
                        "Here you can customise the property so that it fits best to your organisation. A revert button is available next to each setting, so you can revert to the original template version, your changes will be lost. Do not forget to save your changes when you are done. You can move to configure the the next or previous property with the buttons at the bottom of the screen.",
                        { ns: "QuoteItem" }
                      )}
                    </Box>
                  }
                >
                  <InfoIcon />
                </Tooltip>
              </Stack>
            }
            handleClose={handleClose}
          />
          <ModalContent>
            <Form id={formId}>
              <Stack direction={"column"} spacing={2}>
                <CollapseSection
                  title={
                    <Stack component={"span"} direction={"row"} spacing={1}>
                      <span>
                        {t("Property information", { ns: "QuoteItem" })}
                      </span>
                      <Tooltip
                        title={
                          <Box component="span" sx={{ whiteSpace: "pre-line" }}>
                            {t(
                              "In the section Property information you can see some aspects of the property which can't be customised. They are available as reference in order to better understand the context of the configurations.",
                              {
                                ns: "QuoteItem",
                              }
                            )}
                          </Box>
                        }
                      >
                        <InfoIcon />
                      </Tooltip>
                    </Stack>
                  }
                >
                  <Stack spacing={0.5}>
                    <LabeledValue label={t("Name", { ns: "Global" })}>
                      {property.label}
                    </LabeledValue>
                    <LabeledValue label={t("Type", { ns: "QuoteItem" })}>
                      {capitalize(getPropertyType(property))}
                    </LabeledValue>
                    <LabeledValue label={t("Prompt", { ns: "QuoteItem" })}>
                      {property.prompt}
                    </LabeledValue>
                    <LabeledValue
                      label={t("Allow multiple", { ns: "QuoteItem" })}
                    >
                      {isArrayType(property)
                        ? t("Yes", { ns: "Global" })
                        : t("No", { ns: "Global" })}
                    </LabeledValue>
                  </Stack>
                </CollapseSection>

                <CollapseSection
                  title={
                    <Stack component={"span"} direction={"row"} spacing={1}>
                      <span>{t("Property values", { ns: "QuoteItem" })}</span>
                      <Tooltip
                        title={
                          <Box component="span" sx={{ whiteSpace: "pre-line" }}>
                            {t(
                              "If the property has a list of allowed values, you can see them here. If you deselect some values they will be excluded as options for this template. Values that are deselected can not be set for the property in the quote and will not be available to wizard users for selection. You can edit the picture that should be shown on the wizard for each value. You can define a default for the property so that it will be preselected in the quote as an initial value.",
                              {
                                ns: "QuoteItem",
                              }
                            )}
                          </Box>
                        }
                      >
                        <InfoIcon />
                      </Tooltip>
                    </Stack>
                  }
                >
                  <Stack spacing={2}>
                    {propAllowedValues.length > 0 && (
                      <Box>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems={"center"}
                          justifyContent={"space-between"}
                        >
                          <Typography variant="body2" color="text.secondary">
                            {t("Allowed values", { ns: "QuoteItem" })}
                          </Typography>
                          <IconButton
                            color="primary"
                            size="small"
                            sx={{ flexGrow: 0, flexShrink: 0 }}
                            disabled={
                              !formikProps.values.allowedValuesExclude.length
                            }
                            onClick={async () => {
                              await formikProps.setFieldValue(
                                "allowedValuesExclude",
                                []
                              );
                            }}
                          >
                            <Tooltip title={t("Reset", { ns: "Global" })}>
                              <RestartAltIcon />
                            </Tooltip>
                          </IconButton>
                        </Stack>
                        <FormGroup>
                          {propAllowedValues.map(propAllowedValue => {
                            const checked =
                              formikProps.values.allowedValuesExclude?.every(
                                ave => ave !== propAllowedValue.value
                              ) ?? true;
                            const valueAsString =
                              propAllowedValue.value !== null
                                ? propAllowedValue.value.toString()
                                : "null";
                            const overriddenMedia =
                              formikProps.values.allowedValuesMedia?.find(
                                avm => avm.valueAsString === valueAsString
                              )?.media;
                            return (
                              <AllowedValueFormControlLabel
                                key={`prop-allowed-value-${valueAsString}`}
                                checked={checked}
                                overriddenMedia={overriddenMedia}
                                valueAsString={valueAsString}
                                formikProps={formikProps}
                                onAllowedValueChange={onAllowedValueChange}
                                onAllowedValueMediaChange={
                                  onAllowedValueMediaChange
                                }
                                propAllowedValue={propAllowedValue}
                                propertyType={propertyType}
                              />
                            );
                          })}
                        </FormGroup>
                        <FormikError
                          formikProps={formikProps}
                          name="allowedValuesExclude"
                        />
                      </Box>
                    )}

                    {propertyType === "BOOLEAN" &&
                      propValuesMediaBool.length > 0 && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {t("Values", { ns: "QuoteItem" })}
                          </Typography>
                          <Stack direction="column" spacing={1}>
                            {propValuesMediaBool.map(propAllowedValue => {
                              const valueAsString =
                                propAllowedValue.value !== null
                                  ? propAllowedValue.value.toString()
                                  : "null";
                              const overriddenMedia =
                                formikProps.values.allowedValuesMedia?.find(
                                  avm => avm.valueAsString === valueAsString
                                )?.media;
                              return (
                                <ValueLabel
                                  key={`prop-value-media-bool-${valueAsString}`}
                                  overriddenMedia={overriddenMedia}
                                  valueAsString={valueAsString}
                                  formikProps={formikProps}
                                  onValueMediaChange={onAllowedValueMediaChange}
                                  propertyType={propertyType}
                                  media={propAllowedValue.media}
                                  value={propAllowedValue.value}
                                />
                              );
                            })}
                          </Stack>
                        </Box>
                      )}

                    {(() => {
                      let newProperty = property;
                      if (formikProps.values.defaultValue !== undefined) {
                        newProperty = setPropValue(
                          newProperty,
                          formikProps.values.defaultValue
                        );
                      }
                      if (formikProps.values.allowedValuesExclude.length > 0) {
                        newProperty = setPropAllowedValues(
                          newProperty,
                          allowedValue =>
                            !formikProps.values.allowedValuesExclude.includes(
                              // @ts-ignore
                              getAllowedValue(allowedValue)
                            )
                        );
                      }
                      return (
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Stack direction="column" spacing={1} flex={1}>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Box display="flex" flex={1}>
                                <PropertyField
                                  label={t("Default value", {
                                    ns: "QuoteItem",
                                  })}
                                  projectId={null}
                                  docId={templateId}
                                  itemId={itemId}
                                  property={newProperty}
                                  setPropertyValue={async entryValue => {
                                    await formikProps.setFieldValue(
                                      "defaultValue",
                                      getEntryValue(entryValue)
                                    );
                                  }}
                                  disableExpressions
                                />
                              </Box>
                              <PropertyNumberSliderField
                                property={newProperty}
                                onChange={async value => {
                                  await formikProps.setFieldValue(
                                    "defaultValue",
                                    value
                                  );
                                }}
                              />
                            </Stack>
                            <FormikError
                              formikProps={formikProps}
                              name="defaultValue"
                            />
                          </Stack>
                          <IconButton
                            color="primary"
                            size="small"
                            sx={{ flexGrow: 0, flexShrink: 0 }}
                            disabled={
                              formikProps.values.defaultValue === undefined
                            }
                            onClick={async () => {
                              await formikProps.setFieldValue(
                                "defaultValue",
                                undefined
                              );
                            }}
                          >
                            <Tooltip title={t("Reset", { ns: "Global" })}>
                              <RestartAltIcon />
                            </Tooltip>
                          </IconButton>
                        </Stack>
                      );
                    })()}
                  </Stack>
                </CollapseSection>

                <CollapseSection
                  title={
                    <Stack component={"span"} direction={"row"} spacing={1}>
                      <span>
                        {t("Property visibility", { ns: "QuoteItem" })}
                      </span>
                      <Tooltip
                        title={
                          <Box component="span" sx={{ whiteSpace: "pre-line" }}>
                            {t(
                              "The property visibility determines if a property will be shown on the quote document or not. If the visibility is selected, the property will be shown on the quote. If the visibility is not selected, the property will remain hidden. Regardless of this setting, you can still ask the property in the wizard if you want, the visibility will have no impact on it.",
                              {
                                ns: "QuoteItem",
                              }
                            )}
                          </Box>
                        }
                      >
                        <InfoIcon />
                      </Tooltip>
                    </Stack>
                  }
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CheckboxField
                      name="clientVisibility"
                      label={t("Visibility", { ns: "Global" })}
                      value={
                        formikProps.values.clientVisibility !== null
                          ? formikProps.values.clientVisibility
                          : property.clientVisibility
                      }
                      fullWidth
                    />

                    <IconButton
                      color="primary"
                      size="small"
                      sx={{ flexGrow: 0, flexShrink: 0 }}
                      disabled={formikProps.values.clientVisibility === null}
                      onClick={async () => {
                        await formikProps.setFieldValue(
                          "clientVisibility",
                          null
                        );
                      }}
                    >
                      <Tooltip title={t("Reset", { ns: "Global" })}>
                        <RestartAltIcon />
                      </Tooltip>
                    </IconButton>
                  </Stack>
                </CollapseSection>

                {(() => {
                  const questionControl =
                    formikProps.values.questionControl ??
                    property.questionControl;
                  return (
                    <CollapseSection
                      title={
                        <Stack component={"span"} direction={"row"} spacing={1}>
                          <span>
                            {t("Question control", { ns: "QuoteItem" })}
                          </span>
                          <Tooltip
                            title={
                              <Box
                                component="span"
                                sx={{ whiteSpace: "pre-line" }}
                              >
                                {t(
                                  "The question control determines in which wizard a question is shown and to whom. You can set up additional configurations and remove existing ones.",
                                  {
                                    ns: "QuoteItem",
                                  }
                                )}
                              </Box>
                            }
                          >
                            <InfoIcon />
                          </Tooltip>
                        </Stack>
                      }
                      isInitiallyExpanded={true}
                      ActionButtons={
                        <IconButton
                          color="primary"
                          size="small"
                          sx={{ flexGrow: 0, flexShrink: 0 }}
                          disabled={formikProps.values.questionControl === null}
                          onClick={async () => {
                            await formikProps.setFieldValue(
                              "questionControl",
                              null
                            );
                          }}
                        >
                          <Tooltip title={t("Reset", { ns: "Global" })}>
                            <RestartAltIcon />
                          </Tooltip>
                        </IconButton>
                      }
                    >
                      <Stack direction="column" spacing={1}>
                        {questionControl.map((qc, idx, arr) => {
                          return (
                            <div key={idx}>
                              <Stack direction={"row"} spacing={1}>
                                <SelectMultipleField
                                  name={`questionControl[${idx}].askWhen`}
                                  value={
                                    !formikProps.values.questionControl
                                      ? questionControl[idx].askWhen
                                      : undefined
                                  }
                                  onChange={
                                    !formikProps.values.questionControl
                                      ? newValue => {
                                          const newQuestionControl = [
                                            ...questionControl,
                                          ].map((qc, i) => {
                                            if (i === idx) {
                                              return {
                                                ...qc,
                                                askWhen: newValue,
                                              };
                                            }
                                            return qc;
                                          });
                                          formikProps.setFieldValue(
                                            "questionControl",
                                            newQuestionControl
                                          );
                                        }
                                      : undefined
                                  }
                                  label={t("Ask when", { ns: "QuoteItem" })}
                                  options={askWhenOptions}
                                  disabled={formikProps.isSubmitting}
                                />
                                <SelectMultipleField
                                  name={`questionControl[${idx}].askWhom`}
                                  value={
                                    !formikProps.values.questionControl
                                      ? questionControl[idx].askWhom
                                      : undefined
                                  }
                                  onChange={
                                    !formikProps.values.questionControl
                                      ? newValue => {
                                          const newQuestionControl = [
                                            ...questionControl,
                                          ].map((qc, i) => {
                                            if (i === idx) {
                                              return {
                                                ...qc,
                                                askWhom: newValue,
                                              };
                                            }
                                            return qc;
                                          });
                                          formikProps.setFieldValue(
                                            "questionControl",
                                            newQuestionControl
                                          );
                                        }
                                      : undefined
                                  }
                                  options={askWhomOptions}
                                  label={t("Ask whom", { ns: "QuoteItem" })}
                                  disabled={formikProps.isSubmitting}
                                />
                                <IconButton
                                  color="primary"
                                  sx={{ alignSelf: "flex-start" }}
                                  onClick={() => {
                                    const newValue = [...questionControl];
                                    newValue.splice(idx, 1);
                                    formikProps.setFieldValue(
                                      "questionControl",
                                      newValue
                                    );
                                  }}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Stack>
                              {idx < arr.length - 1 ? (
                                <Typography color="grey.600" variant="caption">
                                  {t("or", { ns: "Global" })}
                                </Typography>
                              ) : null}
                            </div>
                          );
                        })}
                        <Button
                          variant="text"
                          color="secondary"
                          startIcon={<AddIcon />}
                          sx={{ alignSelf: "flex-start" }}
                          onClick={() => {
                            formikProps.setFieldValue("questionControl", [
                              ...questionControl,
                              { askWhen: [], askWhom: [] },
                            ]);
                          }}
                        >
                          {t("Add", { ns: "Global" })}
                        </Button>
                      </Stack>
                    </CollapseSection>
                  );
                })()}
              </Stack>
            </Form>
          </ModalContent>
          <DialogActions sx={{ justifyContent: "space-between" }}>
            <Button
              onClick={handlePrevious ?? undefined}
              disabled={formikProps.isSubmitting || formikProps.dirty}
              sx={{ visibility: handlePrevious ? "visible" : "hidden" }}
            >
              {t("Previous", { ns: "Global" })}
            </Button>
            <Stack direction={"row"} spacing={1}>
              <Button variant="text" color="secondary" onClick={handleClose}>
                {t("Close", { ns: "Global" })}
              </Button>
              <Button
                type="submit"
                form={formId}
                variant="contained"
                color="primary"
                loading={formikProps.isSubmitting}
                disabled={!formikProps.isValid || !formikProps.dirty}
              >
                {t("Save", {
                  ns: "Global",
                })}
              </Button>
            </Stack>
            <Button
              onClick={handleNext ?? undefined}
              disabled={formikProps.isSubmitting || formikProps.dirty}
              sx={{ visibility: handleNext ? "visible" : "hidden" }}
            >
              {t("Next", { ns: "Global" })}
            </Button>
          </DialogActions>
        </ModalDialog>
      )}
    </Formik>
  );
}

function PropertyNumberSliderField({
  property,
  onChange,
}: {
  property: Props2;
  onChange: (number: number) => Promise<void>;
}) {
  const language = useTolgee(["language"]).getLanguage()!;
  return isNonComputedProp(property) &&
    isNumberType(property) &&
    !isArrayType(property) &&
    !property.allowedValuesNumber.length &&
    property.range?.min !== null &&
    property.range?.min !== undefined &&
    property.range?.max !== null &&
    property.range?.max !== undefined ? (
    <Stack direction="row" spacing={3} pt={1} alignItems="center" flex={1}>
      <Box pl={2}>
        <Typography variant="body2">
          {getFormattedFloat(
            property.range.min,
            language,
            property.step ?? undefined
          )}
        </Typography>
      </Box>
      <Slider
        color="secondary"
        value={(getPropValue(property) as number | null) ?? property.range.min}
        onChange={async (event, newValue) => {
          if (typeof newValue === "number") {
            await onChange(newValue);
          }
        }}
        valueLabelDisplay="auto"
        min={property.range.min}
        max={property.range.max}
        step={property.step ?? 0.01}
        sx={{ minWidth: "80px" }}
      />
      <Box pr={2}>
        <Typography variant="body2">
          {getFormattedFloat(
            property.range.max,
            language,
            property.step ?? undefined
          )}
        </Typography>
      </Box>
    </Stack>
  ) : null;
}

export function hasPropValuesOverride(values: FormValues) {
  return (
    values.defaultValue !== undefined ||
    values.questionControl !== null ||
    values.clientVisibility !== null ||
    values.allowedValuesExclude.length > 0 ||
    values.allowedValuesMedia.length > 0
  );
}

export function getAllowedValue(
  allowedValue: Props2AllowedValuesNumber | Props2AllowedValuesText
): string | number {
  if ("allowedText" in allowedValue) return allowedValue.allowedText;
  if ("allowedNumber" in allowedValue) return allowedValue.allowedNumber;
  throw new Error("Wrong allowed value provided");
}

export function getEntryValue(
  value: EnterItemProps2ValueEntry
): string | number | string[] | number[] | boolean | null {
  if (value.bool) return value.bool.valueBool ?? null;
  if (value.text) return value.text.valueText ?? null;
  if (value.textArray) return value.textArray.valueTextArray ?? null;
  if (value.number) return value.number.valueNumber ?? null;
  if (value.numberArray) return value.numberArray.valueNumberArray ?? null;
  throw new Error("Wrong value entry provided");
}

function AllowedValueFormControlLabel({
  checked,
  overriddenMedia,
  valueAsString,
  formikProps,
  onAllowedValueMediaChange,
  onAllowedValueChange,
  propAllowedValue,
  propertyType,
}: {
  checked: boolean;
  valueAsString: string;
  overriddenMedia: Attachment | null | undefined;
  formikProps: FormikProps<FormValues>;
  onAllowedValueMediaChange: (
    formikProps: FormikProps<FormValues>,
    valueAsString: string,
    media: Attachment | null | undefined
  ) => Promise<void>;
  onAllowedValueChange: (
    formikProps: FormikProps<FormValues>,
    checked: boolean,
    allowedValue: AllowedValue
  ) => Promise<void>;
  propAllowedValue: AllowedValue;
  propertyType: PropType;
}) {
  return (
    <FormControlLabel
      sx={{ mr: 0 }}
      control={
        <Checkbox
          checked={checked}
          onChange={async e => {
            await onAllowedValueChange(
              formikProps,
              e.target.checked,
              propAllowedValue
            );
          }}
        />
      }
      slotProps={{ typography: { sx: { flex: 1 } } }}
      label={
        <ValueLabel
          overriddenMedia={overriddenMedia}
          valueAsString={valueAsString}
          formikProps={formikProps}
          onValueMediaChange={onAllowedValueMediaChange}
          propertyType={propertyType}
          media={propAllowedValue.media}
          value={propAllowedValue.value}
        />
      }
    />
  );
}

function ValueLabel({
  overriddenMedia,
  valueAsString,
  formikProps,
  onValueMediaChange,
  propertyType,
  media,
  value,
}: {
  valueAsString: string;
  overriddenMedia: Attachment | null | undefined;
  formikProps: FormikProps<FormValues>;
  onValueMediaChange: (
    formikProps: FormikProps<FormValues>,
    valueAsString: string,
    media: Attachment | null | undefined
  ) => Promise<void>;
  propertyType: PropType;
  media: Attachment | null;
  value: string | number | boolean | null;
}) {
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  const { getBoolLabel } = usePropertyHelpers();

  const attachmentInputRef = React.useRef<HTMLInputElement>(null);
  const attachmentPhotoUploaderRef =
    React.useRef<AttachmentPhotoUploaderWithEditorRef>(null);

  const language = useTolgee(["language"]).getLanguage()!;
  const { t } = useTranslate(["Global", "QuoteItem"]);

  return (
    <Stack direction="row" spacing={1} alignItems="center" flex={1}>
      {overriddenMedia !== undefined ? (
        overriddenMedia ? (
          <Badge color="secondary" variant="dot">
            <PropertyGalleryBox
              pictures={[overriddenMedia]
                .map(processAttachment)
                .filter(isImage)}
              flexGrow={0}
              flexShrink={0}
              width="80px"
              height="60px"
              imageHeight={60}
              aspectRatio="80 / 60"
              notInStack
              my={0.25}
              objectFit="contain"
              handleRotate={async (attachment, direction) => {
                await onValueMediaChange(formikProps, valueAsString, {
                  ...overriddenMedia,
                  url: getRotatedUrl(attachment.url, direction),
                });
              }}
              handleDelete={async () => {
                await onValueMediaChange(formikProps, valueAsString, null);
              }}
            />
          </Badge>
        ) : (
          <Badge color="secondary" variant="dot">
            <ImageNotSupportedIcon color="disabled" />
          </Badge>
        )
      ) : (
        media && (
          <PropertyGalleryBox
            pictures={[media].map(processAttachment).filter(isImage)}
            flexGrow={0}
            flexShrink={0}
            width="80px"
            height="60px"
            imageHeight={60}
            aspectRatio="80 / 60"
            notInStack
            my={0.25}
            objectFit="contain"
            handleRotate={async (attachment, direction) => {
              await onValueMediaChange(formikProps, valueAsString, {
                ...omit(media, "__typename"),
                url: getRotatedUrl(attachment.url, direction),
              });
            }}
            handleDelete={async () => {
              await onValueMediaChange(formikProps, valueAsString, null);
            }}
          />
        )
      )}
      <Box display="flex" flex={1}>
        <Typography>
          {value === null
            ? "–"
            : propertyType === "NUMBER"
              ? getFormattedNumber(value as number, language)
              : propertyType === "BOOLEAN"
                ? getBoolLabel(value as boolean)
                : (value as string)}
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        onClick={e => {
          e.preventDefault();
          e.stopPropagation();
        }}
      >
        <>
          <IconButton
            size="small"
            color="secondary"
            disabled={isSubmitting}
            onClick={async () => {
              if (Capacitor.isNativePlatform()) {
                attachmentPhotoUploaderRef.current?.start();
              } else {
                attachmentInputRef.current?.click();
              }
            }}
          >
            {isSubmitting ? (
              <CircularProgress color="inherit" size={24} />
            ) : (
              <AddAPhotoIcon />
            )}
          </IconButton>
          {Capacitor.isNativePlatform() && (
            <AttachmentPhotoUploaderWithEditor
              ref={attachmentPhotoUploaderRef}
              onStart={() => {
                setIsSubmitting(true);
              }}
              onAttachment={async attachment => {
                if (attachment) {
                  await onValueMediaChange(
                    formikProps,
                    valueAsString,
                    attachment
                  );
                }
                setIsSubmitting(false);
              }}
            />
          )}
          <AttachmentUploader
            innerRef={attachmentInputRef}
            accept={"image/*,.heic,.heif"}
            multiple={false}
            onStart={() => {
              setIsSubmitting(true);
            }}
            onComplete={async attachment => {
              if (attachment) {
                await onValueMediaChange(
                  formikProps,
                  valueAsString,
                  attachment
                );
              }
              setIsSubmitting(false);
            }}
          />
        </>
        {overriddenMedia !== undefined && (
          <IconButton
            color="primary"
            size="small"
            sx={{ flexGrow: 0, flexShrink: 0 }}
            onClick={async () => {
              await onValueMediaChange(formikProps, valueAsString, undefined);
            }}
          >
            <Tooltip title={t("Reset", { ns: "Global" })}>
              <RestartAltIcon />
            </Tooltip>
          </IconButton>
        )}
      </Stack>
    </Stack>
  );
}
